import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useCustomPackages, useDeleteCustomPackage } from '@/api/domains/custom-packages';
import type { CustomPackage } from '@/api/types/custom-packages';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toApiProblem, toastProblem } from '@/lib/errors';

import { CustomPackageFormDialog } from '../components/CustomPackageFormDialog';

type Props = {
  dirtyRef: (dirty: boolean) => void;
};

export function CustomPackagesTab({ dirtyRef }: Props) {
  const { t } = useTranslation('settings');
  const tCommon = useTranslation('common').t;
  const packages = useCustomPackages();
  const remove = useDeleteCustomPackage();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CustomPackage | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CustomPackage | null>(null);

  // These tabs persist via dialogs, so there is never unsaved page-level state.
  useEffect(() => dirtyRef(false), [dirtyRef]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (pkg: CustomPackage) => {
    setEditing(pkg);
    setFormOpen(true);
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    remove.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success(t('customPackages.deleted'));
        setDeleteTarget(null);
      },
      onError: (error) => toastProblem(toApiProblem(error)),
    });
  };

  const rows = packages.data ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h3 className="text-xl font-medium tracking-[-0.6px] text-black">
            {t('customPackages.title')}
          </h3>
          <p className="text-base text-muted-foreground">{t('customPackages.description')}</p>
        </div>
        <Button type="button" onClick={openCreate}>
          <Plus className="size-4" />
          {t('customPackages.add')}
        </Button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-[#e5e5e5]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('customPackages.fields.name')}</TableHead>
              <TableHead>{t('customPackages.dimensions')}</TableHead>
              <TableHead>{t('customPackages.fields.weight')}</TableHead>
              <TableHead className="w-44 text-right">{tCommon('actions.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {packages.isLoading ? (
              <TableRow>
                <TableCell colSpan={4} className="py-8 text-center text-sm text-muted-foreground">
                  {tCommon('states.loading')}
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="py-8 text-center text-sm text-muted-foreground">
                  {t('customPackages.empty')}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((pkg) => (
                <TableRow key={pkg.id}>
                  <TableCell className="font-medium text-black">{pkg.name}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {`${pkg.height} × ${pkg.width} × ${pkg.length} ${pkg.dimension_unit.toLowerCase()}`}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {`${pkg.weight} ${pkg.weight_unit.toLowerCase()}`}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        title={tCommon('actions.edit')}
                        onClick={() => openEdit(pkg)}
                      >
                        <Pencil className="size-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        title={tCommon('actions.delete')}
                        onClick={() => setDeleteTarget(pkg)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <CustomPackageFormDialog open={formOpen} onOpenChange={setFormOpen} pkg={editing} />

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('customPackages.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('customPackages.deleteDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tCommon('actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={remove.isPending}>
              {tCommon('actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
